import React from 'react'
import PropTypes from 'prop-types'
import { generatePath } from 'react-router'
import classnames from 'classnames'

import config from 'config'
import { analytics, apiManager } from 'helpers'
import { ROUTES } from 'helpers/constants'
import { useAuthState, useBreakpoints, useCustomerSupportWidget, useLogout, useQuery } from 'hooks'

import CryptoIcon from 'components/icons/CryptoIcon'
import GearIcon from 'components/GearIcon'
import HostIcon from 'components/HostIcon'
import IOSDownloadLink from 'components/IOSDownloadLink'
import SupportIcon from 'components/SupportIcon'
import TicketAltIcon from 'components/TicketAltIcon'
import UserAltIcon from 'components/UserAltIcon'
import WaveIcon from 'components/WaveIcon'

import DropdownItem from './DropdownItem'

const showLiveSupport = false

const ICON_STYLE = 'w-5 h-5 flex-shrink-0'
const { WALLETS, PROFILE, SETTINGS, TICKETS, HOST_PORTAL, EVENT, ADMIN_PORTAL, MEMBERSHIP } = ROUTES

const Notification = ({ className, children }) => (
  <div
    className={classnames(
      ICON_STYLE,
      className,
      'flex items-center justify-center rounded-full bg-rose rounded-full text-xs text-white font-bold text-center'
    )}
  >
    {children}
  </div>
)

const Dropdown = ({ hideMobileNav }) => {
  const { user } = useAuthState()
  const logout = useLogout()
  const { md } = useBreakpoints()
  const customerSupport = useCustomerSupportWidget()

  const { data: events } = useQuery('upcoming-events', apiManager.getDashboardEvents, {
    enabled: !!user,
    refetchOnWindowFocus: false,
  })

  const { data } = useQuery('discover/events', apiManager.getDiscoverEvents, {
    enabled: !!user,
    refetchOnWindowFocus: false,
  })
  const featuredEvent = data?.featuredEvents?.filter(event => !event.signedUp)[0]

  const logOut = e => {
    e.preventDefault()
    analytics.segmentTrack(`${user.Host ? 'Host' : 'User'} Logged Out`)
    logout()
    hideMobileNav && hideMobileNav()
  }

  const LiveSupport = () => (
    <DropdownItem
      to="#"
      isActive={() => false}
      onClick={e => {
        e.preventDefault()
        customerSupport.show()
      }}
      icon={<SupportIcon className={ICON_STYLE} />}
    >
      Live Support
    </DropdownItem>
  )

  return (
    <article
      className="z-1000 top-24 right-10 flex flex-col bg-black overflow-hidden md:py-2 md:absolute md:w-56 md:rounded-lg md:shadow-light"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="options-menu"
    >
      {!md && <>{!user && <LiveSupport />}</>}
      <>
        <DropdownItem
          onClick={hideMobileNav}
          to={PROFILE}
          icon={<UserAltIcon className={ICON_STYLE} />}
        >
          Edit Application
        </DropdownItem>
        {featuredEvent && (
          <DropdownItem
            onClick={hideMobileNav}
            to={generatePath(EVENT, { event_id: featuredEvent.slug })}
            icon={<Notification>1</Notification>}
          >
            {md ? (
              'Invitations'
            ) : (
              <div className="flex items-center justify-center">
                <span className="mr-3">Experience Invitations</span>
                <Notification>1</Notification>
              </div>
            )}
          </DropdownItem>
        )}
        {events?.length > 0 && (
          <DropdownItem
            onClick={hideMobileNav}
            to={TICKETS}
            icon={<TicketAltIcon className={ICON_STYLE} />}
          >
            My tickets
          </DropdownItem>
        )}
        <DropdownItem
          onClick={hideMobileNav}
          to={SETTINGS}
          icon={<GearIcon className={ICON_STYLE} />}
        >
          Settings
        </DropdownItem>
        <DropdownItem
          onClick={hideMobileNav}
          to={MEMBERSHIP}
          icon={<GearIcon className={ICON_STYLE} />}
        >
          Membership
        </DropdownItem>
        {!config.waitlist && (
          <DropdownItem
            onClick={hideMobileNav}
            to={WALLETS}
            icon={<CryptoIcon className={ICON_STYLE} />}
          >
            My Wallets
          </DropdownItem>
        )}
        {user.admin && (
          <DropdownItem
            onClick={hideMobileNav}
            to={ADMIN_PORTAL}
            icon={<HostIcon className={ICON_STYLE} />}
          >
            Admin View
          </DropdownItem>
        )}
        {user.Host?.id && (
          <DropdownItem
            onClick={hideMobileNav}
            to={HOST_PORTAL}
            icon={<HostIcon className={ICON_STYLE} />}
          >
            Host View
          </DropdownItem>
        )}
        {showLiveSupport && <LiveSupport />}
      </>
      {md ? (
        <DropdownItem
          to="/"
          onClick={logOut}
          isActive={() => false}
          data-cy="logout-btn"
          className="md:border-white md:border-opacity-20 md:border-solid md:border-t border-0"
          icon={<WaveIcon className={ICON_STYLE} />}
        >
          Log out
        </DropdownItem>
      ) : (
        <div className="mt-10">
          <div className="mb-4 flex justify-center">
            <IOSDownloadLink className="h-11" />
          </div>
          <button
            className="px-2 py-4 rounded-xl bg-greyNav w-full mb-5 mt-3 text-center text-white text-opacity-50 bg-transparent outline-none"
            onClick={logOut}
          >
            Log out
          </button>
        </div>
      )}
    </article>
  )
}

DropdownItem.propTypes = {
  hideMobileNav: PropTypes.func,
  featuredEvent: PropTypes.object,
}

export default Dropdown
